import { FunctionComponent, useEffect, useMemo, useState } from 'react';
import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Stack,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
} from '@mui/material';
import { fetchAuthWrapper } from '@starjack/api';
import {
    PdfBackground,
    PDF_BACKGROUND_SCOPE_LABELS,
    PDF_BACKGROUND_TYPE_LABELS,
} from '@/models/pdfBackground';
import {
    pdfBackgroundPreviewUrl,
    pdfBackgroundUrl,
} from '@/api/v2/pdfBackground';

type Props = {
    background: PdfBackground | null;
    onClose: () => void;
};

type Mode = 'preview' | 'raw';

export const PdfBackgroundPreviewDialog: FunctionComponent<Props> = ({
    background,
    onClose,
}) => {
    const [mode, setMode] = useState<Mode>('preview');
    const [blobUrl, setBlobUrl] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const open = background !== null;

    const url = useMemo(() => {
        if (!background) return null;
        const params = {
            userId: background.userId,
            shortProductId: background.shortProductId,
        };
        return mode === 'preview'
            ? pdfBackgroundPreviewUrl(background.type, params)
            : pdfBackgroundUrl(background.type, params);
    }, [background, mode]);

    useEffect(() => {
        if (!open) {
            setMode('preview');
            setError(null);
            return;
        }
        if (!url) return;
        let cancelled = false;
        let createdUrl: string | null = null;

        const fetchPdf = async () => {
            setLoading(true);
            setError(null);
            try {
                const response = await fetchAuthWrapper(url, {
                    headers: { Accept: 'application/pdf' },
                });
                if (!response.ok) {
                    throw new Error('PDF konnte nicht geladen werden.');
                }
                const blob = await response.blob();
                if (cancelled) return;
                createdUrl = URL.createObjectURL(blob);
                setBlobUrl(createdUrl);
            } catch {
                if (!cancelled) setError('PDF konnte nicht geladen werden.');
            } finally {
                if (!cancelled) setLoading(false);
            }
        };

        fetchPdf();

        return () => {
            cancelled = true;
            if (createdUrl) URL.revokeObjectURL(createdUrl);
        };
    }, [url, open]);

    useEffect(() => {
        return () => {
            if (blobUrl) URL.revokeObjectURL(blobUrl);
        };
    }, [blobUrl]);

    if (!background) return null;

    return (
        <Dialog open={open} onClose={onClose} maxWidth='lg' fullWidth>
            <DialogTitle>
                Vorschau: {PDF_BACKGROUND_TYPE_LABELS[background.type]}
                <Typography variant='body2' color='text.secondary'>
                    {PDF_BACKGROUND_SCOPE_LABELS[background.scope]}
                    {background.userId && ` · User ${background.userId}`}
                    {background.shortProductId &&
                        ` · Produkt ${background.shortProductId}`}
                </Typography>
            </DialogTitle>
            <DialogContent dividers>
                <Stack
                    direction='row'
                    spacing={2}
                    alignItems='center'
                    sx={{ mb: 2 }}
                >
                    <ToggleButtonGroup
                        size='small'
                        exclusive
                        value={mode}
                        onChange={(_, v) => v && setMode(v)}
                    >
                        <ToggleButton value='preview'>
                            Mit Platzhaltern
                        </ToggleButton>
                        <ToggleButton value='raw'>Nur Hintergrund</ToggleButton>
                    </ToggleButtonGroup>
                </Stack>

                {mode === 'preview' && (
                    <Typography
                        variant='body2'
                        color='text.secondary'
                        sx={{ mb: 2 }}
                    >
                        Die Vorschau enthält Beispieldaten und ein
                        „VORSCHAU“-Wasserzeichen - sie wird nicht so gedruckt.
                        Sie zeigt, wo die fix eingedruckten Texte und Rahmen
                        landen, damit sich prüfen lässt, ob das Design Platz
                        lässt.
                    </Typography>
                )}

                <Box
                    sx={{
                        position: 'relative',
                        minHeight: 600,
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 1,
                    }}
                >
                    {loading && (
                        <Box
                            sx={{
                                position: 'absolute',
                                inset: 0,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                            }}
                        >
                            <CircularProgress />
                        </Box>
                    )}

                    {error && !loading && (
                        <Box
                            sx={{
                                position: 'absolute',
                                inset: 0,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                            }}
                        >
                            <Typography color='error'>{error}</Typography>
                        </Box>
                    )}

                    {blobUrl && !loading && !error && (
                        <Box
                            component='iframe'
                            src={blobUrl}
                            title='PDF Vorschau'
                            sx={{
                                width: '100%',
                                height: 700,
                                border: 0,
                                display: 'block',
                            }}
                        />
                    )}
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Schließen</Button>
            </DialogActions>
        </Dialog>
    );
};
